import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useMatch, useNavigate, useParams } from 'react-router-dom';
import DynamicForm from '@/components/school-management/Form/DynamicForm/DynamicForm';
import { FieldConfig, FormConfig } from '@/school-management/config/formConfig';
import Button from '@/components/school-management/Button/Button';
import styles from './styles.module.scss';
import ActionNotification from '@/components/school-management/ActionNotification/ActionNotification';
import commonUtils from '@/school-management/utils/common';
import { classService } from '@/school-management/services/class';
import { teacherService } from '@/school-management/services/teacher';
import { useAppDispatch, useAppSelector } from '@/store';
import { authActions, authSelector } from '@/store/reducers/auth';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { Class, TeacherReportItem } from '@/school-management/types/response';
import { progressService } from '@/services/progress';
import {
  buildClassFromTeacherOverview,
  parseTeacherOverviewResponse,
} from '@/components/ClassOverview/teacherOverviewClass';
import LevelControlFields from '@/components/school-management/LevelControlFields/LevelControlFields';
import {
  LEVEL_CONTROL_DEFAULTS,
  LevelControlValues,
  isLevelControlValid,
  normalizeStartingLevel,
} from '@/school-management/utils/levelControl';

const buildEditClassFormConfig = (
  teachers: { userId: string; firstName: string; lastName: string; email?: string | null }[],
  t: (key: string) => string,
  onLoadMore?: () => void,
  hasMore?: boolean,
  isLoadingMore?: boolean,
  organizationId?: string,
  enableAutocomplete?: boolean,
  includeTeachersField = true,
  teachersFieldOptions?: {
    label?: string;
    maxSelections?: number;
    required?: boolean;
    excludeValues?: string[];
  },
  isLoadingOptions?: boolean
): FormConfig => {
  const gradeOptions = commonUtils.getAllGradesForDropdown(t).map(grade => ({
    label: grade.label,
    value: grade.id,
  }));

  const baseFields: FieldConfig[] = [
    {
      type: 'title',
      name: 'main-title',
      MainTitle: t('schoolDetailsPage.classes.editClass'),
      gridSpan: 12,
    },
    {
      type: 'text',
      name: 'className',
      label: t('schoolDetailsPage.classes.className'),
      placeholder: t('schoolDetailsPage.classes.className'),
      gridSpan: 4,
      maxLength: 40,
      enforceMaxLength: false,
      validation: {
        required: true,
        pattern: /^[A-Za-z0-9\s'"()–\-:/&*.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
        messages: {
          pattern: t('editClassInvalidClassNameFormat'),
        },
      },
    },
    {
      type: 'select',
      name: 'grade',
      label: t('schoolDetailsPage.classes.gradeLabel'),
      placeholder: t('schoolDetailsPage.classes.gradeLabel'),
      options: gradeOptions,
      gridSpan: 4,
      validation: { required: true },
    },
  ];

  if (includeTeachersField) {
    const teacherOptions = teachers.map(te => ({
      label: `${te.firstName} ${te.lastName}`,
      value: te.userId,
      data: { email: te.email },
    }));

    baseFields.push({
      type: 'multi-select',
      name: 'teachers',
      label: teachersFieldOptions?.label ?? t('schoolDetailsPage.classes.teachersLabel'),
      placeholder: t('schoolDetailsPage.classes.teachersPlaceholder'),
      searchPlaceholder: t('schoolDetailsPage.classes.teachersSearchPlaceholder'),
      options: teacherOptions,
      autocompleteStartOptions: teacherOptions,
      gridSpan: 4,
      maxSelections: teachersFieldOptions?.maxSelections ?? 2,
      maxHeight: '500px',
      validation: { required: teachersFieldOptions?.required ?? true },
      onLoadMore,
      hasMore,
      isLoadingMore,
      enableAutocomplete,
      organizationId,
      autocompleteType: 'teachers',
      excludeValues: teachersFieldOptions?.excludeValues,
      isLoadingOptions,
    } as FieldConfig);
  }

  return {
    onSubmit: () => {},
    fields: baseFields,
  };
};

export default function EditClassFormPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const teacherEditMatch = useMatch('/dashboard/my-classes/edit-class/:classId');
  const isTeacherMode = Boolean(teacherEditMatch);
  const { schoolId, classId } = useParams<{ schoolId?: string; classId: string }>();
  const { user } = useAppSelector(authSelector);
  const organizationId = user?.metadata?.organizationDetails?.organizationId;
  const teachersScopeId = organizationId || schoolId || '';
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [notifType, setNotifType] = useState<'success' | 'error' | 'info'>('success');
  const [classData, setClassData] = useState<Class | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [firstPageTeachers, setFirstPageTeachers] = useState<TeacherReportItem[]>([]);
  const [additionalTeachers, setAdditionalTeachers] = useState<TeacherReportItem[]>([]);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set([1])); // Track which pages are loaded
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(true);
  const [isSeveing, setIsSeveing] = useState<boolean>(false);
  const [levelControlValues, setLevelControlValues] =
    useState<LevelControlValues>(LEVEL_CONTROL_DEFAULTS);
  const levelControlInitializedRef = useRef(false);

  const { t } = useAdminTranslation();

  const currentTeacherId = user?.metadata?.id;

  useEffect(() => {
    const fetchFirstPageInfo = async () => {
      if (!teachersScopeId) return;
      setIsLoadingFirstPage(true);
      try {
        const response = await teacherService.getAllTeachers(teachersScopeId, { page: 1 });
        setTotalPages(response.data?.totalPages ?? 1);
        setFirstPageTeachers(response.data?.items ?? []);
      } catch (error) {
        console.error('Error fetching teachers page info:', error);
        setFirstPageTeachers([]);
      } finally {
        setIsLoadingFirstPage(false);
      }
    };
    fetchFirstPageInfo();
  }, [teachersScopeId]);

  useEffect(() => {
    const fetchClassData = async () => {
      if (!classId) return;

      if (isTeacherMode) {
        if (!user) {
          setIsLoading(false);
          return;
        }
        try {
          const raw = await progressService.fetchOverviewClassProgress(user, classId);
          const cp = parseTeacherOverviewResponse(raw);
          if (cp) {
            setClassData(buildClassFromTeacherOverview(cp, classId, user, undefined));
          } else {
            setNotifMessage(t('dynamicForm.notification.generalError') || '');
            setNotifType('error');
            setNotifOpen(true);
          }
        } catch (error) {
          console.error('Error fetching class data:', error);
          setNotifMessage(t('dynamicForm.notification.generalError') || '');
          setNotifType('error');
          setNotifOpen(true);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      if (!schoolId) return;

      try {
        const response = await classService.getClassById(schoolId, classId);

        if (response.data?.status === 'success') {
          setClassData(response.data.data);
        } else {
          console.error('API response not successful:', response.data);
          setNotifMessage(t('dynamicForm.notification.generalError') || '');
          setNotifType('error');
          setNotifOpen(true);
        }
      } catch (error) {
        console.error('Error fetching class data:', error);
        setNotifMessage(t('dynamicForm.notification.generalError') || '');
        setNotifType('error');
        setNotifOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClassData();
  }, [classId, schoolId, t, isTeacherMode, currentTeacherId]);

  // Seed the level control fields from the loaded class data once (each further change is
  // confirmed individually by LevelControlFields itself, see below).
  useEffect(() => {
    if (!classData || levelControlInitializedRef.current) return;

    levelControlInitializedRef.current = true;
    setLevelControlValues({
      placementTestRequired:
        classData.placementTestRequired ?? LEVEL_CONTROL_DEFAULTS.placementTestRequired,
      defaultStartingLevel:
        normalizeStartingLevel(classData.defaultStartingLevel) ??
        LEVEL_CONTROL_DEFAULTS.defaultStartingLevel,
      allowIndividualAdvancement:
        classData.allowIndividualAdvancement ?? LEVEL_CONTROL_DEFAULTS.allowIndividualAdvancement,
    });
  }, [classData]);

  // Fetch selected teachers separately if they're not in the first page
  useEffect(() => {
    const fetchSelectedTeachers = async () => {
      if (!classData || !teachersScopeId || firstPageTeachers.length === 0) return;

      const selectedTeacherIds = (classData.teachers?.map(t => t.id) || []).filter(
        teacherId => !isTeacherMode || teacherId !== currentTeacherId
      );
      if (selectedTeacherIds.length === 0) {
        if (!isTeacherMode) {
          setAdditionalTeachers([]);
        }
        return;
      }

      const missingTeacherIds = selectedTeacherIds.filter(
        teacherId => !firstPageTeachers.some(t => t.userId === teacherId)
      );

      if (missingTeacherIds.length === 0) {
        if (!isTeacherMode) {
          setAdditionalTeachers([]);
        }
        return;
      }

      try {
        const teacherPromises = missingTeacherIds.map(async teacherId => {
          const response = await teacherService.getAllTeachers(teachersScopeId, { teacherId });
          return { teacherId, response };
        });
        const results = await Promise.all(teacherPromises);
        const fetchedTeachers: TeacherReportItem[] = [];

        for (const result of results) {
          const { teacherId, response } = result;
          if (response.data?.items && response.data.items.length > 0) {
            const teacher = response.data.items.find(t => t.userId === teacherId);
            if (teacher) {
              fetchedTeachers.push(teacher);
            }
          }
        }

        setAdditionalTeachers(fetchedTeachers);
      } catch (error) {
        console.error('Error fetching selected teachers:', error);
      }
    };

    fetchSelectedTeachers();
  }, [classData, teachersScopeId, firstPageTeachers, isTeacherMode, currentTeacherId]);

  const allTeachers = useMemo(() => {
    const teachersMap = new Map<string, TeacherReportItem>();

    (firstPageTeachers || []).forEach(teacher => {
      teachersMap.set(teacher.userId, teacher);
    });

    additionalTeachers.forEach(teacher => {
      teachersMap.set(teacher.userId, teacher);
    });

    // Convert GroupTeacher to TeacherReportItem format
    if (classData?.teachers) {
      classData.teachers.forEach(groupTeacher => {
        if (!teachersMap.has(groupTeacher.id)) {
          teachersMap.set(groupTeacher.id, {
            userId: groupTeacher.id,
            firstName: groupTeacher.firstName,
            lastName: groupTeacher.lastName,
            email: null, // Email not available in GroupTeacher
          } as TeacherReportItem);
        }
      });
    }

    return Array.from(teachersMap.values()).filter(
      teacher => !isTeacherMode || teacher.userId !== currentTeacherId
    );
  }, [firstPageTeachers, additionalTeachers, classData, isTeacherMode, currentTeacherId]);

  const handleLoadMore = useMemo(() => {
    return async () => {
      if (!teachersScopeId || isLoadingMore) return;

      let nextPage = 1;
      for (let i = 2; i <= totalPages; i++) {
        if (!loadedPages.has(i)) {
          nextPage = i;
          break;
        }
      }

      if (nextPage === 1 || nextPage > totalPages) return;

      setIsLoadingMore(true);
      try {
        const response = await teacherService.getAllTeachers(teachersScopeId, { page: nextPage });
        if (response.data?.items) {
          setAdditionalTeachers(prev => {
            const existingIds = new Set([
              ...prev.map(t => t.userId),
              ...(firstPageTeachers || []).map(t => t.userId),
            ]);
            const newTeachers = response.data.items.filter(
              (t: TeacherReportItem) => !existingIds.has(t.userId)
            );
            return [...prev, ...newTeachers];
          });
          setLoadedPages(prev => new Set([...prev, nextPage]));
        }
      } catch (error) {
        console.error('Error loading more teachers:', error);
      } finally {
        setIsLoadingMore(false);
      }
    };
  }, [teachersScopeId, loadedPages, totalPages, isLoadingMore, firstPageTeachers]);

  const hasMoreTeachers = loadedPages.size < totalPages;

  const formConfig = useMemo(() => {
    return buildEditClassFormConfig(
      allTeachers,
      t,
      handleLoadMore,
      hasMoreTeachers,
      isLoadingMore,
      teachersScopeId,
      true,
      true,
      isTeacherMode
        ? {
            label: t('schoolDetailsPage.classes.viewClassPage.coTeacher') || '',
            maxSelections: 1,
            required: false,
            excludeValues: currentTeacherId ? [currentTeacherId] : [],
          }
        : undefined,
      isLoadingFirstPage
    );
  }, [
    allTeachers,
    t,
    handleLoadMore,
    hasMoreTeachers,
    isLoadingMore,
    teachersScopeId,
    isTeacherMode,
    currentTeacherId,
    isLoadingFirstPage,
  ]);

  const initialValues = useMemo(() => {
    if (!classData) return {};

    const resolved = commonUtils.getClassGradeParamForReport(classData, t);
    const gradeId = resolved || null;

    const selectedGradeId = gradeId || '1';
    const gradeTranslationKey = commonUtils.getGradeTranslationKey(selectedGradeId);
    const gradeLabel = t(gradeTranslationKey);

    const selectedGrade = {
      label: gradeLabel,
      value: selectedGradeId,
    };

    const teacherIds = (classData.teachers?.map(t => t.id) || []).filter(
      teacherId => !isTeacherMode || teacherId !== currentTeacherId
    );

    const selectedTeachers = teacherIds
      .map(teacherId => {
        const teacher = allTeachers.find(t => t.userId === teacherId);
        return teacher
          ? {
              label: `${teacher.firstName} ${teacher.lastName}`,
              value: teacher.userId,
              data: { email: teacher.email },
            }
          : null;
      })
      .filter(Boolean);

    const base = {
      className: classData.groupName || '',
      grade: selectedGrade,
    };

    return {
      ...base,
      teachers: selectedTeachers,
    };
  }, [classData, allTeachers, t, isTeacherMode, currentTeacherId]);

  const executeSave = async (values: any) => {
    if (!classId) return;

    const gradeId = String(values.grade?.value ?? values.grade ?? '1');
    const gradeTranslationKey = commonUtils.getGradeTranslationKey(gradeId);
    const gradeName = t(gradeTranslationKey);
    const className = values.className || '';

    setIsSeveing(true);

    if (isTeacherMode) {
      if (!organizationId || !currentTeacherId) {
        setIsSeveing(false);
        return;
      }

      const coTeacherIds: string[] = Array.isArray(values.teachers)
        ? (values.teachers as any[])
            .map(te => (typeof te === 'string' ? te : te?.value))
            .filter((id: any): id is string => Boolean(id))
        : [];

      try {
        const resp = await classService.updateClass(organizationId, classId, {
          groupName: String(className).trim(),
          gradeId,
          teacherIds: [currentTeacherId, ...coTeacherIds.filter(id => id !== currentTeacherId)],
          expectedLastUpdateAt: classData?.lastUpdateAt,
          ...levelControlValues,
        });
        const isSuccess = resp?.data?.status === 'success';

        if (isSuccess) {
          dispatch(
            authActions.updateTeacherClass({
              groupId: classId,
              groupName: String(className).trim(),
              grade: gradeName.trim(),
            })
          );
          await new Promise(resolve => setTimeout(resolve, 2000));
          navigate(`/dashboard/my-classes/${classId}`, {
            replace: true,
            state: { notification: { message: t('dynamicForm.notification.classEdited') } },
          });
        } else {
          setNotifMessage(resp?.data?.message || t('dynamicForm.notification.generalError') || '');
          setNotifType('error');
          setNotifOpen(true);
          setIsSeveing(false);
        }
      } catch (e: any) {
        console.error('Update failed:', e);
        setNotifMessage(t('dynamicForm.notification.generalError') || '');
        setNotifType('error');
        setNotifOpen(true);
        setIsSeveing(false);
      }
      return;
    }

    if (!schoolId) {
      setIsSeveing(false);
      return;
    }

    const teacherIds: string[] = Array.isArray(values.teachers)
      ? (values.teachers as any[])
          .map(te => (typeof te === 'string' ? te : te?.value))
          .filter((id: any): id is string => Boolean(id))
      : [];

    const payload = {
      groupName: String(className),
      gradeId: gradeId,
      teacherIds,
      expectedLastUpdateAt: classData?.lastUpdateAt,
      ...levelControlValues,
    };

    try {
      const resp = await classService.updateClass(schoolId, classId, payload);
      const isSuccess = resp?.data?.status === 'success';
      // Wait 2 seconds for DynamoDB stream to update postgres
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (isSuccess) {
        navigate(`/schools/${schoolId}?tab=classes`, {
          replace: true,
          state: { notification: { message: t('dynamicForm.notification.classEdited') } },
        });
      } else {
        setNotifMessage(resp?.data?.message || t('dynamicForm.notification.generalError') || '');
        setNotifType('error');
        setNotifOpen(true);
        setIsSeveing(false);
      }
    } catch (e: any) {
      console.error('Update failed:', e);
      setNotifMessage(t('dynamicForm.notification.generalError') || '');
      setNotifType('error');
      setNotifOpen(true);
      setIsSeveing(false);
    }
  };

  // Each level control change is confirmed individually by LevelControlFields itself (see
  // render below), so submitting the rest of the form doesn't need its own diff/confirm step.
  const handleSubmit = executeSave;

  if (isLoading || isLoadingFirstPage) {
    return (
      <div className={styles.schoolDetailsPage}>
        <div style={{ textAlign: 'center', padding: '40px' }}>{t('common.loading')}</div>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className={styles.schoolDetailsPage}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <h2>{t('schoolDetailsPage.classes.classNotFound')}</h2>
          <Button
            onClick={() =>
              navigate(isTeacherMode ? '/dashboard/my-classes' : `/schools/${schoolId}?tab=classes`)
            }
          >
            {t('schoolDetailsPage.classes.backToClasses')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.schoolDetailsPage}>
      <DynamicForm
        config={formConfig}
        initialValues={initialValues}
        showPopupOnSubmit={false}
        isreturn={true}
        onSubmit={handleSubmit}
      />
      <div
        style={{
          background: '#ffffff',
          borderRadius: 12,
          padding: 24,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
          marginTop: 16,
        }}
      >
        <LevelControlFields
          placementTestRequired={levelControlValues.placementTestRequired}
          defaultStartingLevel={levelControlValues.defaultStartingLevel}
          allowIndividualAdvancement={levelControlValues.allowIndividualAdvancement}
          isNewClass={false}
          onChange={setLevelControlValues}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginTop: 32 }}>
        <Button
          secondary
          onClick={() =>
            navigate(
              isTeacherMode
                ? `/dashboard/my-classes/${classId}`
                : `/schools/${schoolId}?tab=classes`
            )
          }
          style={{ minWidth: 120 }}
          disabled={isSeveing}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => {
            const form = document.querySelector('form');
            if (!form) return;
            const event = new Event('submit', { cancelable: true, bubbles: true });
            (form as HTMLFormElement).dispatchEvent(event);
          }}
          style={{ minWidth: 120 }}
          loading={isSeveing}
          disabled={isSeveing || !isLevelControlValid(levelControlValues)}
        >
          {t('common.save')}
        </Button>
      </div>
      <ActionNotification
        open={notifOpen}
        message={notifMessage}
        type={notifType}
        position='top-center'
        onClose={() => {
          setNotifOpen(false);
        }}
      />
    </div>
  );
}
